import { useEffect, useState, useRef } from 'react';
import { toast } from '../../../util/toasts';
import { useOrderApiV1 } from '../../../clients/quipli-service/order/order-api-v1';
import { Order, OrderProduct, SeisCertificate } from '../../../clients/quipli-service/order/order';
import { DateTime } from 'luxon';
import { displayMoneyFormat } from '../../../util/util';
import { Collapsible, CollapsibleRef } from '../../../components/collapsible/collapsible';
import Icon from '../../../components/icons/icon';
import { Spinner } from '../../../components/spinner/spinner';
import { Typography } from '../../../components/typography/typography';
import classNames from 'classnames';
import { capitalize } from 'lodash';
import { Button } from '../../../components/form/button';
import { OrderSection } from '../order-section/order-section';
import { Location } from '../../../clients/quipli-service/location/location';
import { Divider } from '../../../components/divider/divider';

export const SeisDamageWaiversModal = ({ order, location }: { order: Order; location: Location }) => {
  const { getSeisDamageWaiversByOrderId } = useOrderApiV1();
  const [certs, setCerts] = useState<SeisCertificate[][]>();
  const [hasBeenOpened, setHasBeenOpened] = useState(false);
  const collapsibleRef = useRef<CollapsibleRef>(null);

  useEffect(() => {
    const init = async () => {};
    init();
  }, []);

  useEffect(() => {
    const init = async () => {
      if (hasBeenOpened) {
        const _certs = await getSeisDamageWaiversByOrderId(order.id).catch((e) => {
          console.error(e);
          toast('Failed to load Rental Damage Waiver Insurance', 'error');
          return undefined;
        });
        setCerts(
          Object.values(_certs ?? {}).sort(
            (a, b) =>
              (a.find((c) => c.status.includes('approved'))?.start_date ?? 0) -
              (b.find((c) => c.status.includes('approved'))?.start_date ?? 0),
          ),
        );
      }
    };

    init();
  }, [hasBeenOpened]);

  const claimDownload = () => {
    window.open('https://quipli-service-prod-public.s3.us-east-1.amazonaws.com/rdw_claim_form.pdf');
  };

  return (
    <OrderSection>
      <Collapsible
        ref={collapsibleRef}
        trigger={(isOpen) => (
          <div
            className="flex justify-between items-center cursor-pointer"
            onClick={() => {
              if (!hasBeenOpened) {
                setHasBeenOpened(true);
              }
            }}
          >
            <div className="flex gap-x-[8px] items-center">
              <Typography type="text-md" weight="medium" className="text-grey-800">
                Rental Damage Waiver Insurance
              </Typography>
            </div>

            <div className="flex gap-x-2">
              {certs && Object.keys(certs).length > 0 && (
                <div className="hidden md:flex">
                  <Button
                    className="self-center p-[6px] h-min button button-secondary w-full"
                    dataCy="claim-form-button"
                    onClick={claimDownload}
                  >
                    <span className="flex gap-2 items-center">
                      <Icon icon="Download" />
                      <Typography type="text-sm" weight="semi-bold" className="text-[var(--grey-700)] ">
                        Claim Form
                      </Typography>
                    </span>
                  </Button>
                </div>
              )}

              <Button className="button-tertiary">
                <Icon icon={isOpen ? 'ChevronUp' : 'ChevronDown'} size="small" />
              </Button>
            </div>
          </div>
        )}
      >
        {!certs ? (
          <Spinner />
        ) : certs && Object.keys(certs).length ? (
          <div className="mt-4">
            <div className="min-w-0 w-full flex md:hidden mb-4">
              <Button
                className="self-center p-[6px] h-min button button-secondary w-full"
                dataCy="claim-form-button"
                onClick={claimDownload}
              >
                <span className="flex gap-2 items-center">
                  <Icon icon="Download" />
                  <Typography type="text-sm" weight="semi-bold" className="text-[var(--grey-700)] ">
                    Claim Form
                  </Typography>
                </span>
              </Button>
            </div>
            {Object.values(certs).map((certsOfCertNumber) => {
              const currentCert =
                certsOfCertNumber.find((c) => c.status.toLowerCase().match(/approved.*current/)) ??
                certsOfCertNumber[certsOfCertNumber.length - 1];
              const updateRequestedCert = certsOfCertNumber.find((c) => c.status.includes('update requested'));

              return (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
                  <div className="col-span-full">
                    <Divider />
                  </div>
                  <div className="col-span-full flex  md:col-span-1 w-full">
                    <SeisDamageWaiverRowProductHeader
                      order={order}
                      currentCert={currentCert}
                      updateRequestedCert={updateRequestedCert}
                    />
                  </div>
                  <div className="col-span-full flex mb-2 md:col-span-1 w-full">
                    <SeisDetailsTable
                      currentCert={currentCert}
                      updateRequestedCert={updateRequestedCert}
                      location={location}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center">
            <p>No rental damage waiver insurance found for this order.</p>
          </div>
        )}
      </Collapsible>
    </OrderSection>
  );
};

const productToSeisTitle = (product: OrderProduct | undefined) => {
  // Format will be "Product Name ([Unit/Serial]: #)"
  // TODO: keeping serial number out for now, can add back in if customers want it
  // const serialNumberOrUnit =
  //   product?.assignedUnits.find((u) => u.id === currentCert.product_unit_id)?.serialNumber || 'Unit';
  return `${product?.product.name}`;
};

const SeisDamageWaiverRowProductHeader = ({
  order,
  currentCert,
  updateRequestedCert,
}: {
  order: Order;
  currentCert: SeisCertificate;
  updateRequestedCert: SeisCertificate | undefined;
}) => {
  const product = order.products.find((p) => p.assignedUnits.find((u) => u.id === currentCert.product_unit_id));

  const productTitle = productToSeisTitle(product);

  return (
    <div className="gap-2 mt-2 w-full flex md:items-center">
      <div className="flex flex-col gap-y-2 w-full">
        <Typography type="text-md" weight="semi-bold" className="text-grey-800">
          {productTitle}
        </Typography>

        <div className="min-w-0 flex flex-col">
          <p
            data-cy="seis-status"
            className={classNames(
              'text-left w-full p-1 pl-2 pr-2 rounded-lg w-max text-sm',
              {
                'bg-green-200': currentCert.status.includes('approved') && !updateRequestedCert,
                'text-green-900': currentCert.status.includes('approved') && !updateRequestedCert,
                'bg-yellow-200': updateRequestedCert && updateRequestedCert.status.includes('update requested'),
                'text-yellow-900': updateRequestedCert && updateRequestedCert.status.includes('update requested'),
              },
              'm-0',
              'font-medium',
            )}
          >
            {updateRequestedCert ? capitalize(updateRequestedCert.status) : capitalize(currentCert.status)}
          </p>
        </div>

        <div className="min-w-0 flex flex-col justify-self-start">
          <div className="flex min-w-0">
            <Typography type="text-md" className="text-left text-black-500 flex-shrink-0 flex-6">
              Serial No:&nbsp;
            </Typography>
            <Typography type="text-md" className="text-left text-gray-500 min-w-0 flex-2 break-words">
              {currentCert.manufacturer_serial_number ||
                product?.assignedUnits.find((u) => u.id === currentCert.product_unit_id)?.serialNumber ||
                '-'}
            </Typography>
          </div>
          <div className="flex min-w-0 mt-2">
            <Typography type="text-md" className="text-left text-black-500 flex-shrink-0 flex-6">
              Certificate No:&nbsp;
            </Typography>
            <Typography type="text-md" className="text-left text-gray-500 min-w-0 flex-2 break-words">
              {currentCert.certificate_number}
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );
};

const SeisDetailsTable = ({
  currentCert,
  updateRequestedCert,
  location,
}: {
  currentCert: SeisCertificate;
  updateRequestedCert: SeisCertificate | undefined;
  location: Location;
}) => {
  return (
    <div className="col-span-5 m-0 mt-2 md:m-4 w-full">
      <div className="p-4 bg-gray-100 rounded-md w-full" style={{ border: '1px solid #D4D4D4' }}>
        <div className="gap-4 grid grid-cols-2">
          <div className="min-w-0">
            <Typography type="text-md" className="text-gray-500">
              Start
            </Typography>
            <div title={DateTime.fromMillis(currentCert.start_date).setZone(location.timezone).toFormat('ff')}>
              <Typography type="text-md">
                {DateTime.fromMillis(currentCert.start_date).setZone(location.timezone).toFormat('ff')}
              </Typography>
            </div>
          </div>
          <div className="min-w-0">
            <Typography type="text-md" className="text-gray-500">
              End
            </Typography>
            <div title={DateTime.fromMillis(currentCert.end_date).setZone(location.timezone).toFormat('ff')}>
              <Typography type="text-md">
                {DateTime.fromMillis(currentCert.end_date).setZone(location.timezone).toFormat('ff')}
              </Typography>
            </div>
          </div>
          <div className="min-w-0">
            <Typography type="text-md" className="text-gray-500">
              Rental Cost
            </Typography>
            <div title={displayMoneyFormat(currentCert.retail_rental_amount, location.currency)}>
              <Typography type="text-md">
                {displayMoneyFormat(currentCert.retail_rental_amount, location.currency)}
              </Typography>
            </div>
          </div>
          <div className="min-w-0">
            <Typography type="text-md" className="text-gray-500">
              Cert. Rate
            </Typography>
            <div title={`${currentCert.certificate_rate * 100}%`}>
              <Typography type="text-md">{currentCert.certificate_rate * 100}%</Typography>
            </div>
          </div>
          <div className="min-w-0">
            <Typography type="text-md" className="text-gray-500">
              Deductible
            </Typography>
            <div title={displayMoneyFormat(currentCert.deductible, location.currency)}>
              <Typography type="text-md">{displayMoneyFormat(currentCert.deductible, location.currency)}</Typography>
            </div>
          </div>

          <div className="min-w-0">
            <Typography type="text-md" className="text-gray-500">
              Cert. Cost
            </Typography>
            <div
              title={displayMoneyFormat(
                +(currentCert.certificate_rate * currentCert.retail_rental_amount).toFixed(2),
                location.currency,
              )}
            >
              <Typography type="text-md">
                {displayMoneyFormat(
                  +(currentCert.certificate_rate * currentCert.retail_rental_amount).toFixed(2),
                  location.currency,
                )}
              </Typography>
            </div>
          </div>
        </div>
        {updateRequestedCert && (
          <div className="mt-4">
            <Typography className="text-yellow-500" type="text-md" weight="semi-bold">
              Update Requested:
            </Typography>
            <div className="text-yellow-600 rounded-md gap-4 grid grid-cols-1 sm:grid-cols-3">
              <div className="min-w-0">
                <Typography type="text-xs" className="text-gray-500">
                  Start
                </Typography>
                <div
                  title={DateTime.fromMillis(updateRequestedCert.start_date).setZone(location.timezone).toFormat('ff')}
                >
                  <Typography type="text-sm" className="text-yellow-500">
                    {DateTime.fromMillis(updateRequestedCert.start_date).setZone(location.timezone).toFormat('ff')}
                  </Typography>
                </div>
              </div>
              <div className="min-w-0">
                <Typography type="text-xs" className="text-gray-500">
                  End
                </Typography>
                <div
                  title={DateTime.fromMillis(updateRequestedCert.end_date).setZone(location.timezone).toFormat('ff')}
                >
                  <Typography type="text-sm" className="text-yellow-500">
                    {DateTime.fromMillis(updateRequestedCert.end_date).setZone(location.timezone).toFormat('ff')}
                  </Typography>
                </div>
              </div>
              <div className="min-w-0">
                <Typography type="text-xs" className="text-gray-500">
                  Rental Cost
                </Typography>
                <div title={displayMoneyFormat(updateRequestedCert.retail_rental_amount, location.currency)}>
                  <Typography type="text-sm" className="text-yellow-500">
                    {displayMoneyFormat(updateRequestedCert.retail_rental_amount, location.currency)}
                  </Typography>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
